import { Alert, Progress, useToast } from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { PortfolioCovenantEditCovenantValueContainer } from './PortfolioCovenantEditCovenantValueContainer';
import { PortfolioCovenantEditInputsContainer } from './PortfolioCovenantEditInputsContainer';
import { PortfolioCovenantEditPropertiesContainer } from './PortfolioCovenantEditPropertiesContainer';
import { useGetAthenaTablesForOrganizationQuery } from 'packages/athena/requests/requests.gql.generated';
import { filterAthenaTablesForCreditFacility } from 'packages/athena/ui/utils';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { PortfolioCovenantQueryInput } from 'packages/settings/ui/AddQueryInputModal';
import {
  SettingsFacilityPortfolioCovenantsEditTemplate,
  SettingsFacilityPortfolioCovenantsNoCovenantKeySelected,
} from 'packages/settings/ui/SettingsFacilityPortfolioCovenantsEditTemplate';
import { PortfolioCovenantEditTab } from 'packages/settings/ui/SettingsFacilityPortfolioCovenantsSidebar';
import { SettingsFacilityPortfolioCovenantsTemplate } from 'packages/settings/ui/SettingsFacilityPortfolioCovenantsTemplate';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

type PortfolioCovenantEditContainerProps = {
  organizationId: string;
  creditFacilityId: string;
  wipConfig: WipConfig;
};
export const PortfolioCovenantEditContainer = ({
  organizationId,
  creditFacilityId,
  wipConfig,
}: PortfolioCovenantEditContainerProps) => {
  const toast = useToast();
  const user = useUserAuthOrThrow();
  const query = useQueryParams();
  const covenantKey = query.get('covenantKey');

  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [triggerDate, setTriggerDate] = useState<Util.FullDateStr>(
    commonDate.dateToFullDateString(new Date(), clientTimeZone),
  );
  const [selectedTab, setSelectedTab] = useState<PortfolioCovenantEditTab>(
    'properties',
  );
  const [fullPropertyKey, setFullPropertyKey] = useState<string>();
  const [
    selectedQueryInput,
    setSelectedQueryInput,
  ] = useState<PortfolioCovenantQueryInput | null>(null);

  useEffect(() => {
    const portfolioCovenantIndex = _.findIndex(
      wipConfig.formProps.values.portfolioCovenants,
      (portCov: any) => portCov?.covenantKey === covenantKey,
    );
    setFullPropertyKey(`portfolioCovenants[${portfolioCovenantIndex}]`);
  }, [covenantKey, wipConfig.formProps.values.portfolioCovenants]);

  const allPortfolioCovenantKeys = useMemo(
    () =>
      _.map(
        wipConfig.formProps.values.portfolioCovenants,
        (portCov) => portCov.covenantKey,
      ),
    [wipConfig.formProps.values.portfolioCovenants],
  );

  const {
    data: creditFacilityData,
    loading: isCreditFacilityDataLoading,
  } = useGetCreditFacilityQuery({
    variables: { creditFacilityId },
  });

  const {
    data: athenaTablesData,
    loading: isAthenaTablesDataLoading,
  } = useGetAthenaTablesForOrganizationQuery({
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: { organizationId: user.user.organizationId },
  });

  if (
    !athenaTablesData ||
    isAthenaTablesDataLoading ||
    !creditFacilityData ||
    isCreditFacilityDataLoading
  ) {
    return (
      <>
        <SettingsFacilityPortfolioCovenantsTemplate
          creditFacilityId={creditFacilityId}
        >
          <Alert status="info" mt="24px">
            Loading Portfolio Covenant...
          </Alert>
          <Progress size="xs" isIndeterminate />
        </SettingsFacilityPortfolioCovenantsTemplate>
      </>
    );
  }

  const athenaTablesForCreditFacility = filterAthenaTablesForCreditFacility({
    athenaTables: athenaTablesData.athenaTables,
    creditFacilitySlug: creditFacilityData.creditFacility.slug,
  });

  if (!covenantKey || !fullPropertyKey) {
    return (
      <SettingsFacilityPortfolioCovenantsNoCovenantKeySelected
        creditFacilityId={creditFacilityId}
      />
    );
  }

  const portfolioCovenant = getIn(wipConfig.formProps.values, fullPropertyKey);

  return (
    <SettingsFacilityPortfolioCovenantsEditTemplate
      creditFacilityId={creditFacilityId}
      selectedTab={selectedTab}
      setSelectedTab={setSelectedTab}
      covenantKey={covenantKey}
      triggerDate={triggerDate}
      setTriggerDate={setTriggerDate}
      wipConfig={wipConfig}
      selectedQueryInput={selectedQueryInput}
      setSelectedQueryInput={setSelectedQueryInput}
      fullPropertyKey={fullPropertyKey}
      portfolioCovenant={portfolioCovenant}
      allPortfolioCovenantKeys={allPortfolioCovenantKeys}
    >
      {selectedTab === 'properties' && (
        <PortfolioCovenantEditPropertiesContainer
          wipConfig={wipConfig}
          fullPropertyKey={fullPropertyKey}
          portfolioCovenant={portfolioCovenant}
          preSelectedTriggerDate={triggerDate}
        />
      )}
      {selectedTab === 'covenantValue' && (
        <PortfolioCovenantEditCovenantValueContainer
          wipConfig={wipConfig}
          fullPropertyKey={fullPropertyKey}
          triggerDate={triggerDate}
          intermediateFields={portfolioCovenant._config_.intermediateFields}
        />
      )}
      {selectedTab === 'inputs' && (
        <PortfolioCovenantEditInputsContainer
          organizationId={organizationId}
          wipConfig={wipConfig}
          fullPropertyKey={fullPropertyKey}
          triggerDate={triggerDate}
          portfolioCovenant={portfolioCovenant}
          selectedQueryInput={selectedQueryInput}
          setSelectedQueryInput={setSelectedQueryInput}
          setSelectedTab={setSelectedTab}
          athenaTables={athenaTablesForCreditFacility}
        />
      )}
    </SettingsFacilityPortfolioCovenantsEditTemplate>
  );
};
